/**
 * Shared measure "Number format" + "Color" fields.
 *
 * Used by both the Single App measure editor (MeasureDetailForm) and the Sync
 * Group measure form (GroupMeasureForm) so alignment and custom-format support
 * stay in one place. Labels are passed in by the caller so each surface can use
 * its own i18n namespace.
 *
 * `numberFormat` and `color` are plain strings (the raw Qlik mask, e.g.
 * "#,##0.00", and a hex color). Empty string means "Auto" / no color.
 */

import { useEffect, useRef, useState } from "react";
import {
  Button,
  Field,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@h2j-data-team/ui";

/** Sentinel for the "Auto" option — Radix Select forbids an empty-string item value. */
const AUTO_FORMAT = "auto";
/** Sentinel for the "Custom" option — reveals a free-text mask input. */
const CUSTOM_FORMAT = "__custom__";

const NUMBER_FORMAT_OPTIONS: { label: string; value: string }[] = [
  { label: "Auto", value: AUTO_FORMAT },
  { label: "Integer", value: "#,##0" },
  { label: "Number", value: "#,##0.00" },
  { label: "Percent", value: "#,##0%" },
  { label: "Percent 2dp", value: "#,##0.00%" },
  { label: "Money", value: "$#,##0.00" },
];

const PRESET_VALUES = new Set(
  NUMBER_FORMAT_OPTIONS.filter((o) => o.value !== AUTO_FORMAT).map((o) => o.value),
);

/** A non-empty value that isn't one of the presets is a user-typed custom mask. */
function isCustomMask(value: string): boolean {
  return value !== "" && !PRESET_VALUES.has(value);
}

export function MeasureNumberFormatField({
  value,
  onChange,
  idPrefix,
  disabled,
  label,
  customOptionLabel,
  maskPlaceholder = "#,##0.00",
}: {
  value: string;
  onChange: (v: string) => void;
  idPrefix: string;
  disabled?: boolean;
  label: string;
  customOptionLabel: string;
  maskPlaceholder?: string;
}) {
  const [customMode, setCustomMode] = useState(() => isCustomMask(value));
  // Tracks the last value THIS field emitted, so the sync effect below can tell
  // an external change (switching the selected item) from our own edits.
  const lastEmitted = useRef<string | null>(null);

  // Re-derive custom mode only on external value changes — never while the user
  // is typing a mask (which could transiently equal a preset and collapse the
  // input mid-edit).
  useEffect(() => {
    if (value !== lastEmitted.current) {
      setCustomMode(isCustomMask(value));
    }
  }, [value]);

  const emit = (v: string) => {
    lastEmitted.current = v;
    onChange(v);
  };

  const selectValue = customMode ? CUSTOM_FORMAT : value === "" ? AUTO_FORMAT : value;

  const handleSelect = (next: string) => {
    if (next === CUSTOM_FORMAT) {
      setCustomMode(true);
      // Starting from a preset: clear it so the user types a fresh mask.
      // Starting from Auto ("") or an existing custom mask: keep the value.
      if (PRESET_VALUES.has(value)) emit("");
    } else {
      setCustomMode(false);
      emit(next === AUTO_FORMAT ? "" : next);
    }
  };

  return (
    <div className="grid gap-2">
      <Field label={label} id={`${idPrefix}-number-format`}>
        {(control) => (
          <Select value={selectValue} onValueChange={handleSelect} disabled={disabled}>
            <SelectTrigger {...control}>
              <SelectValue placeholder="Auto" />
            </SelectTrigger>
            <SelectContent>
              {NUMBER_FORMAT_OPTIONS.map((opt) => {
                const isAuto = opt.value === AUTO_FORMAT;
                return (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className={isAuto ? undefined : "font-mono"}>{opt.label}</span>
                    {!isAuto && (
                      <span className="ml-2 font-mono text-xs text-faint">{opt.value}</span>
                    )}
                  </SelectItem>
                );
              })}
              <SelectItem value={CUSTOM_FORMAT}>{customOptionLabel}</SelectItem>
            </SelectContent>
          </Select>
        )}
      </Field>
      {customMode ? (
        <Input
          id={`${idPrefix}-number-format-mask`}
          value={value}
          onChange={(e) => emit(e.target.value)}
          placeholder={maskPlaceholder}
          aria-label={`${label}: ${customOptionLabel}`}
          disabled={disabled}
          className="font-mono"
        />
      ) : (
        value && <p className="font-mono text-xs text-dim">{value}</p>
      )}
    </div>
  );
}

export function MeasureColorField({
  value,
  onChange,
  idPrefix,
  disabled,
  label,
  clearLabel = "Clear",
}: {
  value: string;
  onChange: (v: string) => void;
  idPrefix: string;
  disabled?: boolean;
  label: string;
  clearLabel?: string;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={`${idPrefix}-color`}>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          id={`${idPrefix}-color`}
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="h-9 w-9 cursor-pointer rounded-input border border-hairline bg-transparent p-0.5 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <span className="font-mono text-sm text-dim">{value || "—"}</span>
        {value && !disabled && (
          <Button type="button" variant="link" size="compact" onClick={() => onChange("")}>
            {clearLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
